/**
 * KAN-956/KAN-964: раздел "Встречи и перезвоны" (дашборд кабинета и вкладка "Встречи" в /m).
 *
 * Сверху шаги выбранного дня (по умолчанию сегодня), внизу кнопка "Просроченные (N)":
 * просроченные скрыты и раскрываются под шагами дня, только когда выбран сегодняшний день.
 * Календарь месяца с числом шагов в днях; клик по дню меняет список и заголовок.
 * Раскладка: "side" - на широком экране календарь справа от списка, на узком под списком
 * по кнопке "Календарь"; "collapsed" (телефон) - всегда под списком по кнопке.
 *
 * Что делать по нажатию на строку решает вызывающий (onOpen: чат клиента в кабинете,
 * карточка клиента в /m). Правила отбора на backend (api.services.today_steps).
 */
import { useState } from 'react'
import { CalendarClock, CalendarDays, ChevronDown, Loader2 } from 'lucide-react'
import type { CallButtonSize } from '@/components/clients/ManagerCallParts'
import { useOrgTimezone } from '@/hooks/useOrgTimezone'
import { useTodaySteps } from '@/hooks/useTodaySteps'
import { monthOf, type MonthRef } from '@/lib/crmRange'
import { TODAY_STEPS_TITLE, todayStepsTitle, zonedToday } from '@/lib/todaySteps'
import type { TodayStepItem } from '@/services/api/todaySteps'
import { TodayStepRow } from './TodayStepRow'
import { TodayStepsCalendar } from './TodayStepsCalendar'

export { TODAY_STEPS_TITLE }

export type TodayStepsCalendarLayout = 'side' | 'collapsed'

interface TodayStepsSectionProps {
  onOpen: (item: TodayStepItem) => void
  /** "lg" - крупная кнопка звонка для телефона (приложение менеджера). */
  callSize?: CallButtonSize
  /** "side" - календарь справа на широком экране (дашборд); "collapsed" - под списком по кнопке (/m). */
  calendarLayout?: TodayStepsCalendarLayout
  className?: string
}

export function TodayStepsSection({
  onOpen,
  callSize = 'sm',
  calendarLayout = 'side',
  className = '',
}: TodayStepsSectionProps) {
  const timeZone = useOrgTimezone()
  /** null - сегодня (запрос без даты, backend считает сегодня по поясу организации). */
  const [selected, setSelected] = useState<string | null>(null)
  const [month, setMonth] = useState<MonthRef | null>(null)
  const [overdueOpen, setOverdueOpen] = useState(false)
  const [calendarOpen, setCalendarOpen] = useState(false)
  const { data, isLoading, isError } = useTodaySteps(selected)

  const today = data?.today ?? zonedToday(timeZone)
  const day = selected ?? today
  const isToday = day === today
  const items = data?.items ?? []
  const overdue = isToday ? data?.overdue ?? [] : []
  const shownMonth = month ?? monthOf(day)
  const side = calendarLayout === 'side'

  const pick = (value: string) => setSelected(value === today ? null : value)
  const row = (item: TodayStepItem) => (
    <TodayStepRow key={item.client_id} item={item} timeZone={timeZone} onOpen={onOpen} callSize={callSize} />
  )
  const calendar = (
    <div className="flex justify-center">
      <TodayStepsCalendar month={shownMonth} onMonthChange={setMonth} selected={day} today={today} onPick={pick} />
    </div>
  )

  return (
    <section
      data-testid="today-steps"
      className={`rounded-xl border border-gray-200 bg-white shadow-sm ${className}`}
    >
      <header className="flex items-center gap-2 border-b border-gray-100 px-4 py-3">
        <CalendarClock className="h-5 w-5 shrink-0 text-blue-600" aria-hidden="true" />
        <h2 className="min-w-0 text-base font-semibold text-gray-900">{todayStepsTitle(day, today)}</h2>
        {items.length > 0 && (
          <span
            data-testid="today-steps-count"
            className="rounded-full bg-blue-600 px-2 py-0.5 text-xs font-semibold text-white"
          >
            {items.length}
          </span>
        )}
        <button
          type="button"
          aria-expanded={calendarOpen}
          onClick={() => setCalendarOpen((open) => !open)}
          className={`ml-auto flex shrink-0 items-center gap-1 rounded-md px-2 py-1 text-sm text-blue-700 hover:bg-blue-50 ${side ? 'lg:hidden' : ''}`}
        >
          <CalendarDays className="h-4 w-4" aria-hidden="true" />
          Календарь
        </button>
      </header>
      <div data-testid="today-steps-body" className={side ? 'flex flex-col lg:flex-row' : 'flex flex-col'}>
        <div className="min-w-0 flex-1">
          {isLoading && (
            <div className="flex justify-center py-6">
              <Loader2 className="h-5 w-5 animate-spin text-gray-400" aria-label="Загрузка" />
            </div>
          )}
          {isError && <p className="px-4 py-6 text-center text-sm text-red-600">Не удалось загрузить список</p>}
          {!isLoading && !isError && items.length === 0 && (
            <p className="px-4 py-6 text-center text-sm text-gray-500">
              {isToday ? 'На сегодня ничего не назначено' : 'На этот день ничего не назначено'}
            </p>
          )}
          {items.length > 0 && <ul className="divide-y divide-gray-100">{items.map(row)}</ul>}
          {overdue.length > 0 && (
            <div className="border-t border-gray-100">
              <button
                type="button"
                aria-expanded={overdueOpen}
                onClick={() => setOverdueOpen((open) => !open)}
                className="flex w-full items-center justify-between px-4 py-3 text-sm font-medium text-red-600 hover:bg-red-50"
              >
                {`Просроченные (${overdue.length})`}
                <ChevronDown className={`h-4 w-4 transition-transform ${overdueOpen ? 'rotate-180' : ''}`} aria-hidden="true" />
              </button>
              {overdueOpen && (
                <ul data-testid="today-steps-overdue" className="divide-y divide-gray-100 border-t border-gray-100">
                  {overdue.map(row)}
                </ul>
              )}
            </div>
          )}
        </div>
        {side ? (
          <div
            data-testid="today-steps-calendar-panel"
            className={`border-t border-gray-100 p-3 lg:block lg:border-l lg:border-t-0 ${calendarOpen ? 'block' : 'hidden'}`}
          >
            {calendar}
          </div>
        ) : (
          calendarOpen && (
            <div data-testid="today-steps-calendar-panel" className="border-t border-gray-100 p-3">
              {calendar}
            </div>
          )
        )}
      </div>
    </section>
  )
}
